import {
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';
import { Link } from 'react-router-dom';
import { CREATION_STYLE_LABELS, type Creation } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import {
  buildGallery,
  creationToItem,
  nativeToItem,
  sortExploreItems,
  type GalleryItem,
} from '../gallery/model.js';
import { NATIVE_GAMES } from '../gallery/nativeGames.js';
import { GameMediaStory, promotionalHighlights } from '../components/GameMediaStory.js';
import { compactCount } from '../components/compactCount.js';
import { LikeButton } from '../components/LikeButton.js';
import { PreviewSoundToggle, useActiveMusicUrl } from '../components/previewMusic.js';
import { useDocumentVisible } from '../hooks/useDocumentVisible.js';

const VIEWED_KEY = 'djgg.explore.viewed';
const SWIPE_HINT_KEY = 'djgg.explore.swipe-hint-count';
const SWIPE_HINT_LIMIT = 2;

export function claimSwipeHint(): boolean {
  try {
    const count = Number.parseInt(localStorage.getItem(SWIPE_HINT_KEY) ?? '0', 10) || 0;
    if (count >= SWIPE_HINT_LIMIT) return false;
    localStorage.setItem(SWIPE_HINT_KEY, String(count + 1));
    return true;
  } catch {
    return false;
  }
}

function restoredViewedIds(): Set<string> {
  try {
    const value = JSON.parse(sessionStorage.getItem(VIEWED_KEY) ?? '[]');
    return new Set(Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function ExploreLike({ item }: { item: GalleryItem }) {
  const { user } = useAuth();
  const services = useServices();
  const liked = useSubscription<boolean>(
    (cb) => (user ? services.subscribe.isLiked(item.id, user.uid, cb) : () => {}),
    false,
    [item.id, user?.uid],
  );
  return (
    <LikeButton
      creationId={item.id}
      initialLiked={liked}
      initialCount={item.likes}
      serverLiked={liked}
      serverCount={item.likes}
    />
  );
}

function ExploreDetails({
  item,
  onClose,
  onPlay,
}: {
  item: GalleryItem;
  onClose: () => void;
  onPlay: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);

  return (
    <div className="explore-sheet__backdrop" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        className="explore-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="explore-sheet-title"
        data-testid="explore-details"
      >
        <div className="explore-sheet__handle" aria-hidden="true" />
        <button ref={closeRef} className="explore-sheet__close" type="button" onClick={onClose} aria-label="Close details">
          ×
        </button>
        <p className="explore-sheet__eyebrow">{CREATION_STYLE_LABELS[item.style]} · by @{item.creatorHandle}</p>
        <h2 id="explore-sheet-title">{item.title}</h2>
        <p className="explore-sheet__description">{item.description}</p>
        <div className="explore-sheet__stats" aria-label="Game statistics">
          <span>▶ {compactCount(item.plays)} plays</span>
          <span>◉ {compactCount(item.views)} views</span>
          <span>♥ {compactCount(item.likes)} likes</span>
          {item.kind === 'creation' && item.forks > 0 && <span>⑂ {compactCount(item.forks)} forks</span>}
          {item.multiplayer && <span>⚔ Multiplayer</span>}
          {item.attachedAssetCount > 0 && <span>✦ {item.attachedAssetCount} artworks</span>}
        </div>
        <div className="explore-sheet__actions">
          <a className="btn btn--primary" href={item.playUrl} onClick={onPlay}>▶ Play</a>
          <ExploreLike item={item} />
          <Link className="btn btn--ghost" to={`/g/${item.slug}`}>Full game page</Link>
        </div>
      </div>
    </div>
  );
}

function ExploreSlide({
  item,
  active,
  nearby,
  index,
  slideRef,
  musicUrl,
  onMore,
  onPlay,
}: {
  item: GalleryItem;
  active: boolean;
  nearby: boolean;
  index: number;
  slideRef: (node: HTMLElement | null) => void;
  /** The active slide's music (undefined while resolving, null = none). */
  musicUrl: string | null | undefined;
  onMore: () => void;
  onPlay: () => void;
}) {
  return (
    <article
      ref={slideRef}
      className="explore-slide"
      data-index={index}
      data-slug={item.slug}
      aria-label={`${item.title}, game ${index + 1}`}
      aria-current={active ? 'true' : undefined}
      aria-hidden={active ? undefined : 'true'}
      inert={!active}
    >
      <div className="explore-slide__stage">
        <GameMediaStory
          slug={item.slug}
          title={item.title}
          coverUrl={nearby ? item.coverUrl : undefined}
          posterUrl={nearby ? item.posterUrl : undefined}
          titleOverlayUrl={nearby ? item.titleOverlayUrl : undefined}
          previewUrl={nearby ? item.previewUrl : undefined}
          previewKind={nearby ? item.previewKind : undefined}
          previewProbeUrl={nearby ? item.previewProbeUrl : undefined}
          demoRef={nearby ? item.demoRef : undefined}
          assetUrls={nearby ? item.storyAssetUrls : undefined}
          highlights={promotionalHighlights(item)}
          active={active}
          variant="feed"
          musicUrl={musicUrl}
        />
        <div className="explore-slide__content">
          <div className="explore-slide__copy">
            <p className="explore-slide__creator">@{item.creatorHandle}</p>
            <h1>{item.title}</h1>
            <p className="explore-slide__description">{item.description}</p>
          </div>
          <div className="explore-slide__controls" aria-label={`${item.title} actions`}>
            <a className="explore-action explore-action--play" href={item.playUrl} onClick={onPlay}>
              <span aria-hidden="true">▶</span><strong>Play</strong>
            </a>
            {active ? <ExploreLike item={item} /> : <span className="explore-action" aria-hidden="true">♥</span>}
            <span className="explore-action explore-action--stat" aria-label={`${item.plays} plays`}>
              <span>{compactCount(item.plays)}</span><strong>plays</strong>
            </span>
            <span className="explore-action explore-action--stat" aria-label={`${item.views} views`}>
              <span>{compactCount(item.views)}</span><strong>views</strong>
            </span>
            <PreviewSoundToggle url={musicUrl} className="explore-action explore-action--sound" showLabel />
            <button className="explore-action" type="button" onClick={onMore} aria-label={`More information about ${item.title}`}>
              <span aria-hidden="true">•••</span><strong>More</strong>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export function Explore() {
  const services = useServices();
  const docs = useSubscription<Array<Creation & { id: string }> | null>(
    services.subscribe.creations,
    null,
    [],
  );
  const [feedSeed] = useState(() => Math.floor(Math.random() * 0x1_0000_0000));
  const feedOrder = useRef<string[]>([]);
  const items = useMemo(() => {
    if (docs === null) return [];
    const nativeDocs = new Map(docs.filter((doc) => doc.kind === 'native').map((doc) => [doc.slug, doc]));
    const creations = docs.filter((doc) => doc.kind !== 'native').map(creationToItem);
    const natives = NATIVE_GAMES.map((game) => nativeToItem(game, nativeDocs.get(game.slug)));
    const available = buildGallery(creations, natives, { sort: 'rated' }).filter(
      (item) => item.kind === 'native' || item.playable,
    );
    const byId = new Map(available.map((item) => [item.id, item]));
    const retained = feedOrder.current.flatMap((id) => byId.get(id) ?? []);
    const known = new Set(feedOrder.current);
    return [
      ...retained,
      ...sortExploreItems(available.filter((item) => !known.has(item.id)), feedSeed),
    ];
  }, [docs, feedSeed]);
  useEffect(() => {
    // Keep counters and metadata live without moving slides under the user.
    // Newly available games join the end; removed/unplayable games drop out.
    feedOrder.current = items.map((item) => item.id);
  }, [items]);
  const feedRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<Array<HTMLElement | null>>([]);
  const ratios = useRef(new Map<number, number>());
  const viewed = useRef(restoredViewedIds());
  const restored = useRef(false);
  const activeSlugRef = useRef<string | null>(null);
  const hintClaimed = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [primarilyVisible, setPrimarilyVisible] = useState(true);
  const [details, setDetails] = useState<GalleryItem | null>(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const pageVisible = useDocumentVisible();
  const closeDetails = useCallback(() => setDetails(null), []);

  useEffect(() => {
    if (docs === null || items.length === 0 || hintClaimed.current) return;
    hintClaimed.current = true;
    setShowSwipeHint(claimSwipeHint());
  }, [docs, items.length]);

  useEffect(() => {
    if (!showSwipeHint) return;
    const timeout = window.setTimeout(() => setShowSwipeHint(false), 3_800);
    return () => window.clearTimeout(timeout);
  }, [showSwipeHint]);

  // Mobile browser chrome changes the visible viewport without reliably
  // changing CSS `vh`. Measure the actual visual viewport so the bottom copy
  // and controls stay above Safari/Chrome toolbars while they expand/collapse.
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const update = () => {
      const height = viewport?.height ?? window.innerHeight;
      root.style.setProperty('--explore-viewport-height', `${Math.round(height)}px`);
    };
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      root.style.removeProperty('--explore-viewport-height');
    };
  }, []);

  useEffect(() => {
    // Wait for the remote catalogue before choosing an identity — the
    // native-only first paint mixes differently from the full feed, so
    // anchoring against it would flash one game, then turn into whichever
    // cloud game later occupied the same numeric index. Every visit opens
    // at the start of its newly mixed feed.
    if (docs === null || items.length === 0 || restored.current) return;
    restored.current = true;
    activeSlugRef.current = items[0]?.slug ?? null;
    setActiveIndex(0);
    requestAnimationFrame(() => slideRefs.current[0]?.scrollIntoView?.({ block: 'start' }));
  }, [docs, items]);

  useEffect(() => {
    if (!restored.current || !activeSlugRef.current) return;
    const anchoredIndex = items.findIndex((item) => item.slug === activeSlugRef.current);
    if (anchoredIndex < 0 || anchoredIndex === activeIndex) return;
    setActiveIndex(anchoredIndex);
    requestAnimationFrame(() => slideRefs.current[anchoredIndex]?.scrollIntoView?.({ block: 'start' }));
  }, [activeIndex, items]);

  useEffect(() => {
    if (items.length > 0 && activeIndex >= items.length) setActiveIndex(items.length - 1);
  }, [activeIndex, items.length]);

  useEffect(() => {
    const root = feedRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    ratios.current.clear();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset.index);
          ratios.current.set(index, entry.intersectionRatio);
        }
        let nextIndex = 0;
        let bestRatio = 0;
        for (const [index, ratio] of ratios.current) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            nextIndex = index;
          }
        }
        const isPrimary = bestRatio >= 0.55;
        setPrimarilyVisible(isPrimary);
        if (isPrimary) {
          activeSlugRef.current = items[nextIndex]?.slug ?? null;
          setActiveIndex(nextIndex);
        }
      },
      { root, threshold: [0.25, 0.55, 0.75] },
    );
    slideRefs.current.forEach((slide) => slide && observer.observe(slide));
    return () => observer.disconnect();
  }, [items]);

  const activeItem = items[activeIndex];
  // Each story carries its game's own loop: the switch is session-wide, the
  // track follows whichever slide holds the screen.
  const activeDoc = activeItem?.kind === 'creation' ? docs?.find((doc) => doc.id === activeItem.id) : undefined;
  const musicUrl = useActiveMusicUrl(activeDoc);
  useEffect(() => {
    if (!activeItem || !pageVisible || !primarilyVisible || details !== null) return;
    if (viewed.current.has(activeItem.id)) return;
    const timeout = window.setTimeout(() => {
      viewed.current.add(activeItem.id);
      sessionStorage.setItem(VIEWED_KEY, JSON.stringify([...viewed.current]));
      services.recordView(activeItem.id).catch(() => {
        viewed.current.delete(activeItem.id);
        sessionStorage.setItem(VIEWED_KEY, JSON.stringify([...viewed.current]));
      });
    }, 5_000);
    return () => window.clearTimeout(timeout);
  }, [activeItem, details, pageVisible, primarilyVisible, services]);

  const move = (delta: number) => {
    setShowSwipeHint(false);
    const next = Math.max(0, Math.min(items.length - 1, activeIndex + delta));
    slideRefs.current[next]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'PageDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      slideRefs.current[0]?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    } else if (event.key === 'End') {
      event.preventDefault();
      slideRefs.current.at(-1)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  };
  const recordPlay = (item: GalleryItem) => {
    services.recordPlay(item.id).catch(() => { /* play navigation must not fail */ });
  };

  if (docs === null) return <div className="explore-empty">Loading the arcade…</div>;
  if (items.length === 0) return <div className="explore-empty">No playable games yet.</div>;

  return (
    <>
      <div
        ref={feedRef}
        className="explore-feed"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onTouchStart={() => setShowSwipeHint(false)}
        onWheel={() => setShowSwipeHint(false)}
        aria-label="Explore games"
        data-testid="explore-feed"
      >
        {showSwipeHint && (
          <div className="explore-swipe-hint" role="status">
            <span aria-hidden="true">↕</span>
            <strong>Swipe to explore</strong>
            <small>Up for the next game · down to go back</small>
          </div>
        )}
        <p className="sr-only" aria-live="polite">
          {activeItem.title}, game {activeIndex + 1} of {items.length}
        </p>
        {items.map((item, index) => (
          <ExploreSlide
            key={item.id}
            item={item}
            index={index}
            active={index === activeIndex}
            nearby={Math.abs(index - activeIndex) <= 1}
            slideRef={(node) => { slideRefs.current[index] = node; }}
            musicUrl={index === activeIndex ? musicUrl : undefined}
            onMore={() => setDetails(item)}
            onPlay={() => recordPlay(item)}
          />
        ))}
      </div>
      {details && <ExploreDetails item={details} onClose={closeDetails} onPlay={() => recordPlay(details)} />}
    </>
  );
}
